/**
 * Product Tour Store
 *
 * Backend-driven product tour system that:
 * - Fetches tour configuration from `/tour/guide/info` API
 * - Auto-activates tours based on `guide_status` and completion state
 * - Persists progress to localStorage for resume after refresh
 * - Supports sequential tour activation (complete tour A → auto-start tour B)
 * - Calls backend APIs on tour completion and review completion
 * - Prevents re-showing tours after page refresh using localStorage flag
 *
 * LocalStorage Schema:
 * Key: `tour_progress_${guide_type}` (e.g., "tour_progress_first_order_after_install")
 * Value: {
 *   completedSteps: string[],  // Array of completed step IDs
 *   currentIndex: number,       // Index of next step to show
 *   lastUpdated: number         // Timestamp
 * }
 *
 * Key: `tour_shown_${guide_type}` (e.g., "tour_shown_first_order_after_install")
 * Value: boolean (true if tour has been shown at least once)
 *
 * Tour Type Mapping:
 * - Backend `first_order_after_install` → Frontend `attribution`
 * - Backend `sync_events` → Frontend `sync`
 */
import { defineStore } from 'pinia'

import type { TourGuideRecord } from '@/services/api/tour.api'

import { tourGuideCompleteApi, tourGuideInfoApi } from '@/services/api/tour.api'

interface TourStep {
  id: string
  selector: string
  text: string
  placement?: 'bottom' | 'right' | 'left' | 'top'
  requiredRoute?: string // Route path required for this step (e.g., '/integrations', '/attribution/all')
}

type TourType = 'sync' | 'attribution' | 'intent_attribution' | 'intent_abandoner' | 'intent_influencer' | 'intent_audiences' | 'intent_anonymous' | 'intent_explore' | 'intent_summary' | 'none'

/**
 * Backend guide_type to Frontend tourType mapping configuration
 *
 * This mapping defines the relationship between backend guide types
 * and frontend tour implementations. Update this object when:
 * - Adding new tour types
 * - Backend changes guide_type naming
 * - Need to map multiple backend types to same frontend tour
 *
 * @example
 * Backend returns: { guide_type: "first_order_after_install" }
 * Frontend uses: tourType = "attribution"
 */
const GUIDE_TYPE_TO_TOUR_TYPE_MAP: Record<string, TourType> = {
  // Attribution tour - triggered when first order is tracked
  first_order_after_install: 'attribution',

  // Sync tour - triggered when first event is synced to destination
  sync_events: 'sync',

  // Virtual tours
  intent_attribution_virtual: 'intent_attribution',
  intent_abandoner_virtual: 'intent_abandoner',
  intent_influencer_virtual: 'intent_influencer',
  intent_audiences_virtual: 'intent_audiences',
  intent_anonymous_virtual: 'intent_anonymous',
  intent_explore_virtual: 'intent_explore',
  intent_summary_virtual: 'intent_summary',
}

/**
 * Tour progress structure stored in localStorage
 */
interface TourProgress {
  completedSteps: string[] // Array of step IDs that user has completed
  currentIndex: number // Index of the next step to show (0-based)
  lastUpdated: number // Timestamp when progress was last saved
}

interface State {
  active: boolean // Is a tour currently active?
  index: number // Current step index (0-based)
  steps: TourStep[] // Steps for current tour
  tourType: TourType // Current tour type
  prePromptOpen: boolean // Is pre-tour prompt visible?
  tourQueue: TourGuideRecord[] // All tours from backend API
  activeTour: TourGuideRecord | null // Currently active tour record
  completedSteps: Record<string, Set<string>> // Map of guide_type → Set of completed step IDs
  reviewRecord: TourGuideRecord | null // Review record from backend (guide_type='review')
}

function getSyncSteps(): TourStep[] {
  return [
    {
      id: 'step1',
      selector: '[data-tour="sidebar-connections"]',
      text: 'Start with your Connections.',
      placement: 'right',
      requiredRoute: '/integrations', // Step 1: Navigate to Connections/Integrations page
    },
    {
      id: 'step2',
      selector: '[data-tour="sidebar-destinations"]',
      text: 'Click Destinations, where we send your enriched data.',
      placement: 'right',
      requiredRoute: '/integrations', // Step 2: Still on Integrations page
    },
    {
      id: 'step3',
      selector: '[data-tour-row="0"] [data-tour="destinations-analysis-icon"]',
      text: 'View data analysis. See enriched customer events of the destination.',
      placement: 'bottom',
      requiredRoute: '/conversion-feed/destinations', // Step 3: On Destinations page
    },
    {
      id: 'step4',
      selector: '[data-tour="date-range-dropdown"]',
      text: 'Use date range to explore how your customer events perform over any period.',
      placement: 'right',
      requiredRoute: '/conversion-feed/data-report', // Step 4: On Data Report page
    },
  ]
}

function getAttributionSteps(): TourStep[] {
  return [
    {
      id: 'attr_step1',
      selector: '[data-tour="sidebar-attribution"]',
      text: 'Click Attribution to view performance.',
      placement: 'right',
      // No requiredRoute - sidebar is visible on any page
    },
    {
      id: 'attr_step2',
      selector: '[data-tour="attribuly-tracked-conversion-value"]',
      text: 'Click the metric to sort it for clearer data insights.',
      placement: 'bottom',
      requiredRoute: '/attribution/all', // Step 2: On Attribution page
    },
    {
      id: 'attr_step3',
      selector: '[data-tour="conversion-value-cell"]',
      text: 'Click the data to view which customers contributed to it.',
      placement: 'bottom',
      requiredRoute: '/attribution/all', // Step 3: Still on Attribution page
    },
    {
      id: 'attr_step4',
      selector: '[data-tour="attribution-model-dropdown"]',
      text: 'Choose attribution models to view how each touchpoint contributes to your conversions.',
      placement: 'bottom',
      requiredRoute: '/attribution/all', // Step 4: Still on Attribution page
    },
    {
      id: 'attr_step5',
      selector: '[data-tour="attribution-goal-dropdown"]',
      text: 'Choose conversion goals for the actions you want to measure.',
      placement: 'bottom',
      requiredRoute: '/attribution/all', // Step 5: Still on Attribution page
    },
    {
      id: 'attr_step6',
      selector: '[data-tour="attribution-dimension-button"]',
      text: 'Select dimensions to view performance by channel, campaign, and more.',
      placement: 'bottom',
      requiredRoute: '/attribution/all', // Step 6: Still on Attribution page
    },
  ]
}

function getIntentAttributionSteps(): TourStep[] {
  return [
    {
      id: 'intent_attr_step1',
      selector: '[data-tour="filter-button"]',
      text: 'Filter by date, goals, and attribution model to analyze performance from different perspectives.',
      placement: 'right', // Points LEFT
      requiredRoute: '/attribution/all',
    },
    {
      id: 'intent_attr_step2',
      selector: '[data-tour="attribution-dimension-button"]',
      text: 'Customize your report columns to focus on the metrics that matter to you.',
      placement: 'left', // Points RIGHT
      requiredRoute: '/attribution/all',
    },
    {
      id: 'intent_attr_step3',
      selector: '[data-tour="attribuly-tracked-conversions-header"]',
      text: 'Click the numbers to see the customers and orders behind each conversion — then select a customer to view their full journey.',
      placement: 'top', // Points DOWN
      requiredRoute: '/attribution/all',
    },
  ]
}

function getIntentAbandonerSteps(): TourStep[] {
  return [
    {
      id: 'intent_abandoner_step1',
      selector: '[data-tour="metric-klaviyo-revenue"]',
      text: 'See how much additional email revenue Attribuly has generated for you in Home section.',
      placement: 'bottom',
      requiredRoute: '/home',
    },
    {
      id: 'intent_abandoner_step2',
      selector: '[data-tour="chart-email-revenue"]',
      text: 'See how your incremental email revenue changes over time.',
      placement: 'top',
      requiredRoute: '/home',
    },
    {
      id: 'intent_abandoner_step3',
      selector: '[data-tour="destinations-actions-header"]',
      text: 'View detailed performance data and update destination settings from the Actions menu.',
      placement: 'left',
      requiredRoute: '/conversion-feed',
    },
  ]
}

function getIntentInfluencerSteps(): TourStep[] {
  return [
    {
      id: 'intent_influencer_step1',
      selector: '[data-tour="intent-influencer-step-1"]',
      text: 'Swipe left to see more metrics, such as on-site actions like Add to Cart.',
      placement: 'top', // Points DOWN
      requiredRoute: '/influencer',
    },
    {
      id: 'intent_influencer_step2',
      selector: '[data-tour="intent-influencer-step-2"]',
      text: 'Customize your report columns to focus on the metrics that matter to you.',
      placement: 'bottom', // Points UP
      requiredRoute: '/influencer',
    },
    {
      id: 'intent_influencer_step3',
      selector: '[data-tour="intent-influencer-step-3"]',
      text: 'Bulk import influencers using a CSV file.',
      placement: 'bottom',
      requiredRoute: '/influencer',
    },
  ]
}

function getIntentAudiencesSteps(): TourStep[] {
  return [
    {
      id: 'intent_audiences_step1',
      selector: '[data-tour="metric-facebook-audience"]',
      text: 'View the incremental audience growth for Meta and Google here.',
      placement: 'top',
      requiredRoute: '/home',
    },
    {
      id: 'intent_audiences_step2',
      selector: '[data-tour="destinations-actions-header"]',
      text: 'View detailed performance data of your connected destinations and edit configurations from the Actions menu.',
      placement: 'top',
      requiredRoute: '/conversion-feed',
    },
    {
      id: 'intent_audiences_step3',
      selector: '[data-tour="btn-add-destination"]',
      text: 'Add more destinations to improve data quality and build larger retargeting audiences with server-side tracking.',
      placement: 'bottom',
      requiredRoute: '/conversion-feed',
    },
  ]
}

function getIntentAnonymousSteps(): TourStep[] {
  return [
    {
      id: 'intent_anonymous_step1',
      selector: '[data-tour="metric-aan-revenue"]',
      text: 'See the anonymous shoppers Attribuly has identified — and the revenue we\'ve generated for you.',
      placement: 'bottom', // "引导弹窗向上指向" -> Arrow points UP -> Tooltip is BELOW.
      requiredRoute: '/home',
    },
    {
      id: 'intent_anonymous_step2',
      selector: '[data-tour="capture-settings-title"]',
      text: 'Manage and adjust your Capture settings here.',
      placement: 'top', // "引导弹窗向下指向" -> Arrow points DOWN -> Tooltip is ABOVE.
      requiredRoute: '/settings/aan',
    },
  ]
}

function getIntentExploreSteps(): TourStep[] {
  return [
    {
      id: 'intent_explore_step1',
      selector: '[data-tour="events-chart-title"]',
      text: 'View all customer actions across your store over time — including how users from marketing channels behave on your site.',
      placement: 'top',
      requiredRoute: '/live-events',
    },
    {
      id: 'intent_explore_step2',
      selector: '[data-tour="event-types-dropdown"]',
      text: 'Customize the metrics shown to focus on what matters most to you.',
      placement: 'bottom',
      requiredRoute: '/live-events',
    },
    {
      id: 'intent_explore_step3',
      selector: '[data-tour="activity-feed-title"]',
      text: 'See individual customer events and the related marketing campaigns. Click a customer name to view their full journey across channels.',
      placement: 'top',
      requiredRoute: '/live-events',
    },
  ]
}

function getIntentSummarySteps(): TourStep[] {
  return [
    {
      id: 'intent_summary_step1',
      selector: '[data-tour="summary-kpi-cards"]',
      text: 'Monitor your key business performance metrics at a glance, including revenue generated by Attribuly.',
      placement: 'bottom',
      requiredRoute: '/home',
    },
    {
      id: 'intent_summary_step2',
      selector: '[data-tour="summary-charts"]',
      text: 'Analyze trends over time to understand your growth trajectory.',
      placement: 'top',
      requiredRoute: '/home',
    },
    {
      id: 'intent_summary_step3',
      selector: '[data-tour="summary-store-analytics"]',
      text: 'Deep dive into your store\'s operational data and customer retention metrics.',
      placement: 'top',
      requiredRoute: '/home',
    },
  ]
}

/**
 * Map backend guide_type to frontend tourType
 *
 * Uses the centralized GUIDE_TYPE_TO_TOUR_TYPE_MAP configuration.
 * Returns 'none' if the guide_type is not recognized.
 *
 * @param guideType - Backend guide_type from API response
 * @returns Frontend tourType or 'none' if not found
 */
function mapGuideTypeToTourType(guideType: string): TourType {
  const tourType = GUIDE_TYPE_TO_TOUR_TYPE_MAP[guideType]

  if (!tourType) {
    console.warn(
      `[ProductTour] Unknown guide_type: "${guideType}". `
      + `Please add mapping to GUIDE_TYPE_TO_TOUR_TYPE_MAP. `
      + `Available types: ${Object.keys(GUIDE_TYPE_TO_TOUR_TYPE_MAP).join(', ')}`,
    )
    return 'none'
  }

  return tourType
}

/**
 * Load tour progress from localStorage
 */
function loadTourProgress(guideType: string): TourProgress | null {
  try {
    const key = `tour_progress_${guideType}`
    const stored = localStorage.getItem(key)
    if (!stored)
      return null
    return JSON.parse(stored) as TourProgress
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to load progress for ${guideType}:`, error)
    return null
  }
}

/**
 * Save tour progress to localStorage
 */
function saveTourProgress(guideType: string, completedSteps: string[], currentIndex: number): void {
  try {
    const key = `tour_progress_${guideType}`
    const progress: TourProgress = {
      completedSteps,
      currentIndex,
      lastUpdated: Date.now(),
    }
    localStorage.setItem(key, JSON.stringify(progress))
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to save progress for ${guideType}:`, error)
  }
}

/**
 * Save active tour metadata to localStorage
 */
function saveActiveTourMeta(tourRecord: TourGuideRecord | null): void {
  try {
    const key = 'tour_active_meta'
    if (tourRecord) {
      localStorage.setItem(key, JSON.stringify(tourRecord))
      console.warn('[ProductTour] Saved tour meta to localStorage:', {
        key,
        guide_type: tourRecord.guide_type,
        guide_status: tourRecord.guide_status,
      })
    }
    else {
      localStorage.removeItem(key)
      console.warn('[ProductTour] Removed tour meta from localStorage')
    }
  }
  catch (error) {
    console.warn('[ProductTour] Failed to save active tour meta:', error)
  }
}

/**
 * Load active tour metadata from localStorage
 */
function loadActiveTourMeta(): TourGuideRecord | null {
  try {
    const key = 'tour_active_meta'
    const stored = localStorage.getItem(key)
    if (!stored)
      return null
    return JSON.parse(stored) as TourGuideRecord
  }
  catch (error) {
    console.warn('[ProductTour] Failed to load active tour meta:', error)
    return null
  }
}

/**
 * Clear tour progress from localStorage
 */
function clearTourProgress(guideType: string): void {
  try {
    const key = `tour_progress_${guideType}`
    localStorage.removeItem(key)
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to clear progress for ${guideType}:`, error)
  }
}

/**
 * Check if a tour has been shown before
 */
function hasTourBeenShown(guideType: string): boolean {
  try {
    const key = `tour_shown_${guideType}`
    const value = localStorage.getItem(key)
    return value === 'true'
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to check if tour was shown for ${guideType}:`, error)
    return false
  }
}

/**
 * Mark a tour as shown
 */
function markTourAsShown(guideType: string): void {
  try {
    const key = `tour_shown_${guideType}`
    localStorage.setItem(key, 'true')
    console.warn(`[ProductTour] Marked tour as shown: ${guideType}`)
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to mark tour as shown for ${guideType}:`, error)
  }
}

/**
 * Clear tour shown flag (for testing or reset purposes)
 */
function clearTourShownFlag(guideType: string): void {
  try {
    const key = `tour_shown_${guideType}`
    localStorage.removeItem(key)
    console.warn(`[ProductTour] Cleared shown flag for: ${guideType}`)
  }
  catch (error) {
    console.warn(`[ProductTour] Failed to clear shown flag for ${guideType}:`, error)
  }
}

export const useProductTourStore = defineStore('productTour', {
  state: (): State => {
    // Try to restore activeTour from localStorage during initialization
    const savedTour = loadActiveTourMeta()
    let initialTourType: TourType = 'none'
    let initialSteps = getSyncSteps()
    let initialIndex = 0
    const initialCompletedSteps: Record<string, Set<string>> = {}

    if (savedTour) {
      // 如果保存的是虚拟导览（intent_..._virtual），则不恢复，视为无效
      // 虚拟导览应该是临时的，刷新后不应自动恢复
      if (savedTour.guide_type.endsWith('_virtual')) {
        console.warn('[ProductTour] Ignoring saved virtual tour:', savedTour.guide_type)
        saveActiveTourMeta(null) // 清理无效的本地存储
      }
      else {
        initialTourType = mapGuideTypeToTourType(savedTour.guide_type)
        if (initialTourType === 'sync') {
          initialSteps = getSyncSteps()
        }
        else if (initialTourType === 'attribution') {
          initialSteps = getAttributionSteps()
        }

        // Restore progress for this tour
        const progress = loadTourProgress(savedTour.guide_type)
        if (progress) {
          initialIndex = progress.currentIndex
          initialCompletedSteps[savedTour.guide_type] = new Set(progress.completedSteps)
          console.warn('[ProductTour] Store initialized with saved tour and progress:', {
            guide_type: savedTour.guide_type,
            currentIndex: initialIndex,
            completedSteps: progress.completedSteps.length,
          })
        }
        else {
          console.warn('[ProductTour] Store initialized with saved tour (no progress):', savedTour.guide_type)
        }
      }
    }

    return {
      active: false,
      index: initialIndex,
      steps: initialSteps,
      tourType: initialTourType,
      prePromptOpen: false,
      tourQueue: [],
      activeTour: savedTour,
      completedSteps: initialCompletedSteps,
      reviewRecord: null,
    }
  },
  actions: {
    /**
     * Fetch tour guide info from backend and auto-activate first eligible tour
     */
    async fetchAndActivateTours() {
      try {
        console.warn('[ProductTour] Fetching tour guide info from backend...')
        const response = await tourGuideInfoApi()

        if (!response?.Records || response.Records.length === 0) {
          console.warn('[ProductTour] No tours available from backend')
          return
        }

        // Store all tours in queue (excluding review record)
        // Extract review record separately (guide_type='review')
        this.reviewRecord = response.Records.find(r => r.guide_type === 'review') || null
        this.tourQueue = response.Records.filter(r => r.guide_type !== 'review')

        console.warn('[ProductTour] Review record:', this.reviewRecord)

        // Sort tours: active (guide_status=1) first
        // Note: We prioritize by guide_status only, as frontend manages step completion
        const sortedTours = [...response.Records].sort((a, b) => {
          // Priority: active tours (guide_status=1) first
          if (a.guide_status === 1 && b.guide_status !== 1)
            return -1
          if (b.guide_status === 1 && a.guide_status !== 1)
            return 1

          // If both have same status, maintain original order
          return 0
        })

        // Find first eligible tour (active status = 1 AND not shown before)
        // Note: We don't check current_guide_step < total_guide_step from backend
        // because frontend controls the actual step count
        const eligibleTour = sortedTours.find(
          tour => tour.guide_status === 1 && !hasTourBeenShown(tour.guide_type),
        )

        if (!eligibleTour) {
          console.warn('[ProductTour] No eligible tours to activate (all tours either inactive or already shown)')
          return
        }

        console.warn('[ProductTour] Activating tour:', eligibleTour.guide_type)
        this.activeTour = eligibleTour
        console.warn('[ProductTour] Set activeTour in store:', this.activeTour)
        saveActiveTourMeta(eligibleTour)

        // Map guide_type to tourType and set steps (frontend controls step count)
        const tourType = mapGuideTypeToTourType(eligibleTour.guide_type)
        if (tourType === 'none') {
          console.warn('[ProductTour] Unknown guide_type:', eligibleTour.guide_type)
          return
        }

        this.setTourType(tourType)

        // Check if user has started this tour before
        const progress = loadTourProgress(eligibleTour.guide_type)
        if (progress && progress.completedSteps.length > 0) {
          // Tour was interrupted - restore progress
          console.warn('[ProductTour] Restored interrupted tour progress:', progress)
          this.completedSteps[eligibleTour.guide_type] = new Set(progress.completedSteps)
          this.index = progress.currentIndex
        }
        else {
          // New tour or no progress yet - start from beginning
          console.warn('[ProductTour] Starting new tour from step 0')
          this.completedSteps[eligibleTour.guide_type] = new Set()
          this.index = 0
          // Clear any stale progress data
          if (progress) {
            clearTourProgress(eligibleTour.guide_type)
          }
        }

        console.warn('[ProductTour] Tour activated:', {
          tourType,
          guideType: eligibleTour.guide_type,
          currentStep: this.index,
          totalSteps: this.steps.length, // Frontend-controlled step count
          backendTotalSteps: eligibleTour.total_guide_step, // Backend value (for reference)
          hasProgress: progress && progress.completedSteps.length > 0,
        })
      }
      catch (error) {
        console.error('[ProductTour] Failed to fetch and activate tours:', error)
      }
    },

    /**
     * Start the next tour in queue
     */
    startNextTour() {
      // Find next eligible tour (active status, not shown before, excluding current tour)
      // Frontend manages step completion, so we only check guide_status
      const nextTour = this.tourQueue.find(
        tour => tour.guide_status === 1
          && tour.guide_type !== this.activeTour?.guide_type
          && !hasTourBeenShown(tour.guide_type),
      )

      if (nextTour) {
        console.warn('[ProductTour] Starting next tour:', nextTour.guide_type)
        this.activeTour = nextTour
        saveActiveTourMeta(nextTour)

        // Map and activate
        const tourType = mapGuideTypeToTourType(nextTour.guide_type)
        if (tourType !== 'none') {
          this.setTourType(tourType)
        }

        // Check if this is an interrupted tour or a new one
        const progress = loadTourProgress(nextTour.guide_type)
        if (progress && progress.completedSteps.length > 0) {
          // Tour was interrupted - restore progress
          console.warn('[ProductTour] Restored interrupted tour progress for next tour')
          this.completedSteps[nextTour.guide_type] = new Set(progress.completedSteps)
          this.index = progress.currentIndex
        }
        else {
          // New tour - start from beginning
          console.warn('[ProductTour] Starting new sequential tour from step 0')
          this.completedSteps[nextTour.guide_type] = new Set()
          this.index = 0
          // Clear any stale progress data
          if (progress) {
            clearTourProgress(nextTour.guide_type)
          }
        }
      }
      else {
        console.warn('[ProductTour] No more tours to activate')
      }
    },

    start() {
      console.warn('[ProductTour] start() called, current state:', {
        activeTour: this.activeTour?.guide_type,
        tourType: this.tourType,
        hasSteps: this.steps.length,
        currentIndex: this.index,
      })

      // activeTour should already be restored during store initialization
      // This is a fallback check
      if (!this.activeTour) {
        console.warn('[ProductTour] WARNING: No activeTour found, attempting restore...')
        const savedTour = loadActiveTourMeta()
        if (savedTour) {
          console.warn('[ProductTour] Restored activeTour from localStorage:', savedTour.guide_type)
          this.activeTour = savedTour
          const tourType = mapGuideTypeToTourType(savedTour.guide_type)
          if (tourType !== 'none') {
            this.tourType = tourType
            this.steps = tourType === 'sync' ? getSyncSteps() : getAttributionSteps()
          }
        }
        else {
          console.error('[ProductTour] CRITICAL: No saved tour found and no activeTour set!')
        }
      }

      // Restore progress from localStorage (currentIndex was already set in fetchAndActivateTours)
      // Don't reset index here - it should already be correct from fetchAndActivateTours
      // If index is still 0 and there's saved progress, restore it
      if (this.activeTour && this.index === 0) {
        const progress = loadTourProgress(this.activeTour.guide_type)
        if (progress && progress.currentIndex > 0) {
          this.index = progress.currentIndex
          this.completedSteps[this.activeTour.guide_type] = new Set(progress.completedSteps)
          console.warn('[ProductTour] Restored progress in start():', {
            currentIndex: this.index,
            completedSteps: progress.completedSteps.length,
          })
        }
      }

      // Mark tour as shown when it starts
      if (this.activeTour) {
        markTourAsShown(this.activeTour.guide_type)
      }

      this.active = true
      this.prePromptOpen = false

      console.warn('[ProductTour] start() complete, final state:', {
        activeTour: this.activeTour?.guide_type,
        tourType: this.tourType,
        active: this.active,
        startingFromStep: this.index,
      })
    },

    next() {
      // Mark current step as completed
      if (this.activeTour && this.steps[this.index]) {
        const stepId = this.steps[this.index].id
        const guideType = this.activeTour.guide_type

        if (!this.completedSteps[guideType]) {
          this.completedSteps[guideType] = new Set()
        }
        this.completedSteps[guideType].add(stepId)

        // Save to localStorage
        const completedArray = Array.from(this.completedSteps[guideType])
        saveTourProgress(guideType, completedArray, this.index + 1)

        console.warn('[ProductTour] Step completed:', stepId, 'Progress saved')
      }

      if (this.index < this.steps.length - 1) {
        this.index++
      }
      else {
        this.finish()
      }
    },

    async finish() {
      console.warn('[ProductTour] Finishing tour')

      // Call backend completion API (only for non-virtual tours)
      if (this.activeTour) {
        // Skip backend API for virtual tours
        if (this.activeTour.guide_type.endsWith('_virtual')) {
          console.warn('[ProductTour] Finishing virtual tour locally (skipping backend API)')

          // Clear localStorage for this tour
          clearTourProgress(this.activeTour.guide_type)
          clearTourShownFlag(this.activeTour.guide_type)
          saveActiveTourMeta(null)
        }
        else {
          try {
            console.warn('[ProductTour] Calling completion API for:', this.activeTour.guide_type)
            const response = await tourGuideCompleteApi({ type: this.activeTour.guide_type })

            if (response && response.code > 0) {
              console.warn('[ProductTour] Tour completion recorded on backend')

              // Clear localStorage for this tour (both progress and shown flag)
              clearTourProgress(this.activeTour.guide_type)
              clearTourShownFlag(this.activeTour.guide_type)
              saveActiveTourMeta(null)

              // Update tour status in queue (mark as completed)
              const tourIndex = this.tourQueue.findIndex(t => t.guide_type === this.activeTour?.guide_type)
              if (tourIndex !== -1) {
                this.tourQueue[tourIndex].guide_status = 2 // Completed
                // Update to frontend's actual step count (not backend's total_guide_step)
                this.tourQueue[tourIndex].current_guide_step = this.steps.length
                console.warn('[ProductTour] Updated tour status to completed')
              }
            }
            else {
              console.warn('[ProductTour] Tour completion API returned error:', response)
              // Retry once after 2 seconds
              await new Promise(resolve => setTimeout(resolve, 2000))
              await tourGuideCompleteApi({ type: this.activeTour.guide_type })
            }
          }
          catch (error) {
            console.error('[ProductTour] Failed to record tour completion:', error)
            // Don't block user, continue anyway
          }
        }
      }

      this.active = false
      this.index = 0

      // Check if we should show review prompt
      // Show review if: review_status is 0 (not reviewed) or 2 (skipped)
      const shouldShowReview = this.reviewRecord
        && (this.reviewRecord.review_status === 0 || this.reviewRecord.review_status === 2)

      if (shouldShowReview) {
        console.warn('[ProductTour] Scheduling review prompt (review_status:', this.reviewRecord?.review_status, ')')
        // Import and show review prompt after 3 seconds
        const reviewStatus = this.reviewRecord?.review_status
        setTimeout(async () => {
          try {
            const { useUserReviewStore } = await import('@/stores/user-review')
            const reviewStore = useUserReviewStore()
            // Pass backend review_status to determine eligibility
            reviewStore.showIfEligible(reviewStatus)
          }
          catch (error) {
            console.error('[ProductTour] Failed to show review prompt:', error)
          }
        }, 3000)
      }
      else {
        console.warn('[ProductTour] Skip review prompt (review_status:', this.reviewRecord?.review_status, ')')
      }

      // Try to start next tour only if the current tour was NOT a virtual/intent tour
      // Virtual tours are user-triggered and should not auto-chain into system tours
      if (this.activeTour && !this.activeTour.guide_type.endsWith('_virtual')) {
        this.startNextTour()
      }
      else {
        console.warn('[ProductTour] Finished virtual tour, not chaining to next tour')
      }
    },

    setTourType(type: TourType) {
      this.tourType = type
      // Set preset steps and open pre-prompt for preview
      if (type === 'sync') {
        this.steps = getSyncSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = true
      }
      else if (type === 'attribution') {
        this.steps = getAttributionSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = true
      }
      else if (type === 'intent_attribution') {
        this.steps = getIntentAttributionSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false // No pre-prompt for intent tour
      }
      else if (type === 'intent_abandoner') {
        this.steps = getIntentAbandonerSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else if (type === 'intent_influencer') {
        this.steps = getIntentInfluencerSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else if (type === 'intent_audiences') {
        this.steps = getIntentAudiencesSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else if (type === 'intent_anonymous') {
        this.steps = getIntentAnonymousSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else if (type === 'intent_explore') {
        this.steps = getIntentExploreSteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else if (type === 'intent_summary') {
        this.steps = getIntentSummarySteps()
        this.index = 0
        this.active = false
        this.prePromptOpen = false
      }
      else {
        this.active = false
        this.index = 0
        this.prePromptOpen = false
      }
    },

    /**
     * Start the intent attribution tour immediately
     */
    startIntentAttributionTour() {
      console.warn('[ProductTour] Starting intent attribution tour')
      this.setTourType('intent_attribution')

      // Clear previous progress for this tour type to ensure it starts from beginning
      // Since this is an on-demand tour triggered by user intent
      // We don't really have a backend "guide_type" for it yet, so let's use a virtual one
      const virtualGuideType = 'intent_attribution_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent abandoner tour immediately
     */
    startIntentAbandonerTour() {
      console.warn('[ProductTour] Starting intent abandoner tour')
      this.setTourType('intent_abandoner')

      const virtualGuideType = 'intent_abandoner_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent influencer tour immediately
     */
    startIntentInfluencerTour() {
      console.warn('[ProductTour] Starting intent influencer tour')
      this.setTourType('intent_influencer')

      const virtualGuideType = 'intent_influencer_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent audiences tour immediately
     */
    startIntentAudiencesTour() {
      console.warn('[ProductTour] Starting intent audiences tour')
      this.setTourType('intent_audiences')

      const virtualGuideType = 'intent_audiences_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent anonymous tour immediately
     */
    startIntentAnonymousTour() {
      console.warn('[ProductTour] Starting intent anonymous tour')
      this.setTourType('intent_anonymous')

      const virtualGuideType = 'intent_anonymous_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent explore tour immediately
     */
    startIntentExploreTour() {
      console.warn('[ProductTour] Starting intent explore tour')
      this.setTourType('intent_explore')

      const virtualGuideType = 'intent_explore_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    /**
     * Start the intent summary tour immediately
     */
    startIntentSummaryTour() {
      console.warn('[ProductTour] Starting intent summary tour')
      this.setTourType('intent_summary')

      const virtualGuideType = 'intent_summary_virtual'

      // Create a virtual active tour record
      this.activeTour = {
        guide_type: virtualGuideType,
        guide_status: 1,
        total_guide_step: this.steps.length,
        current_guide_step: 0,
        team_id: 0,
        review_status: 0,
        params: null,
      }

      this.active = true
      this.prePromptOpen = false
      this.index = 0

      // Clear any stored progress
      clearTourProgress(virtualGuideType)

      saveActiveTourMeta(this.activeTour)
      markTourAsShown(virtualGuideType)
    },

    openPrePrompt() { this.prePromptOpen = true },
    closePrePrompt() { this.prePromptOpen = false },
  },
})
